import React, { useState, useEffect, useContext, useCallback } from "react";
import { Navigate } from "react-router-dom";
import Navbar from "../../../ui/Navbar";
import Modal from "../../../ui/Modal";
import { AuthContext } from "../../../../context/AuthContext";
import Table from "../../../ui/Table";
import Loader from "../../../ui/Loader";
import { swalError, swalSuccess, swalConfirm } from "../../../../utils/swalAlert";
import { fetchWithAuth } from "../../../../utils/api";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";

const serverUrl = `${process.env.REACT_APP_SERVER_URL}`;

const EMPTY_FORM = {
    type: "",
    value: "",
    displayValue: "",
    displayOrder: "",
};

export default function ManageLOVs() {
    const { isAuthenticated, primaryRole } = useContext(AuthContext);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filteredData, setFilteredData] = useState([]);

    // Modal state
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formError, setFormError] = useState("");

    // Form state
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [editingRow, setEditingRow] = useState(null);
    const [isNewType, setIsNewType] = useState(false);

    // Filter state
    const [selectedType, setSelectedType] = useState(null);
    const [availableTypes, setAvailableTypes] = useState([]);

    const breadcrumbs = [
        { name: "Home", path: "/superadmin" },
        { name: "LOVs", path: "/superadmin/lov-management", isLast: true },
    ];

    // ── helpers ──────────────────────────────────────────────────────────────

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const resetForm = () => {
        setFormData(EMPTY_FORM);
        setFormError("");
        setEditingRow(null);
        setIsNewType(false);
    };

    // ── data fetching ─────────────────────────────────────────────────────────

    const fetchLOVs = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await fetchWithAuth(
                `${serverUrl}/api/lov/get-all`,
                { credentials: "include" },
            );
            if (!response) return;
            if (!response.ok) throw new Error("Failed to fetch LOVs");
            const result = await response.json();
            const lovs = Array.isArray(result) ? result : result.data || [];

            // Sort by displayOrder ascending
            const sorted = lovs.sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0));
            setData(sorted);

            // Extract unique types and create options
            const types = [...new Set(sorted.map((lov) => lov.type))].sort((a, b) => (a ?? "").localeCompare(b ?? ""));
            const typeOptions = types.map((t) => ({ value: t, label: t }));
            setAvailableTypes(typeOptions);

            // Filter data based on selected type
            if (selectedType) {
                setFilteredData(sorted.filter((lov) => lov.type === selectedType.value));
            } else {
                setFilteredData(sorted);
            }
        } catch (err) {
            setError(err.message);
            swalError(err.message);
        } finally {
            setLoading(false);
        }
    }, [selectedType]);

    useEffect(() => {
        fetchLOVs();
    }, [fetchLOVs]);

    if (!isAuthenticated || primaryRole !== "superadmin") {
        return <Navigate to="/login" replace />;
    }

    // ── open edit modal ───────────────────────────────────────────────────────

    const openEditModal = (row) => {
        setEditingRow(row);
        setFormData({
            type: row.type || "",
            value: row.value || "",
            displayValue: row.displayValue || "",
            displayOrder: row.displayOrder || "",
        });
        setFormError("");
        setIsNewType(false);
        setIsEditModalOpen(true);
    };

    // ── create ────────────────────────────────────────────────────────────────

    const handleCreateLOV = async (e) => {
        e.preventDefault();
        setFormError("");

        // Validation
        if (!formData.type.trim()) {
            setFormError("Type is required");
            return;
        }
        if (!formData.value.trim()) {
            setFormError("Value is required");
            return;
        }
        if (!formData.displayOrder || isNaN(formData.displayOrder)) {
            setFormError("Display Order must be a valid number");
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await fetchWithAuth(
                `${serverUrl}/api/lov/add`,
                {
                    method: "POST",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        type: formData.type.trim(),
                        value: formData.value.trim(),
                        displayValue: formData.displayValue ? formData.displayValue.trim() : "",
                        displayOrder: Number(formData.displayOrder),
                    }),
                },
            );
            if (!response) return;
            if (!response.ok) {
                const errorData = await response.json();
                if (errorData.errors && Array.isArray(errorData.errors)) {
                    swalError("Validation Error", errorData.errors.map((e) => `<p>${e}</p>`).join(""));
                    return;
                }
                throw new Error(errorData.message || "Failed to create LOV");
            }
            setIsCreateModalOpen(false);
            resetForm();
            swalSuccess("LOV created successfully");
            fetchLOVs();
        } catch (err) {
            setFormError(err.message);
            swalError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── update ────────────────────────────────────────────────────────────────

    const handleUpdateLOV = async (e) => {
        e.preventDefault();
        setFormError("");

        // Validation
        if (!formData.type.trim()) {
            setFormError("Type is required");
            return;
        }
        if (!formData.value.trim()) {
            setFormError("Value is required");
            return;
        }
        if (!formData.displayOrder || isNaN(formData.displayOrder)) {
            setFormError("Display Order must be a valid number");
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await fetchWithAuth(
                `${serverUrl}/api/lov/update/${editingRow._id}`,
                {
                    method: "PUT",
                    credentials: "include",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        type: formData.type.trim(),
                        value: formData.value.trim(),
                        displayValue: formData.displayValue ? formData.displayValue.trim() : "",
                        displayOrder: Number(formData.displayOrder),
                    }),
                },
            );
            if (!response) return;
            if (!response.ok) {
                const errorData = await response.json();
                if (errorData.errors && Array.isArray(errorData.errors)) {
                    swalError("Validation Error", errorData.errors.map((e) => `<p>${e}</p>`).join(""));
                    return;
                }
                throw new Error(errorData.message || "Failed to update LOV");
            }
            setIsEditModalOpen(false);
            resetForm();
            swalSuccess("LOV updated successfully");
            fetchLOVs();
        } catch (err) {
            setFormError(err.message);
            swalError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── delete ────────────────────────────────────────────────────────────────

    const handleDeleteLOV = async (lovId) => {
        const result = await swalConfirm(
            "Delete LOV",
            `Are you sure you want to delete this LOV?\n`,
            "Yes, delete it!",
            "No, Cancel"
        );
        if (result.isConfirmed) {
            try {
                const response = await fetchWithAuth(
                    `${serverUrl}/api/lov/delete/${lovId}`,
                    {
                        method: "DELETE",
                        credentials: "include",
                    },
                );
                if (!response) return;
                if (!response.ok) {
                    const errorData = await response.json();
                    throw new Error(errorData.message || "Failed to delete LOV");
                }
                swalSuccess("LOV deleted successfully");
                fetchLOVs();
            } catch (err) {
                swalError(err.message);
            }
        }
    };

    // ── table columns ─────────────────────────────────────────────────────────

    const columns = [
        {
            key: "type",
            label: "Type",
            sortable: true,
            render: (row) => (
                <span className="status-badge badge-requested">{row.type || "N/A"}</span>
            ),
            filter: true,
        },
        {
            key: "value",
            label: "Value",
            sortable: true,
        },
        {
            key: "displayValue",
            label: "Display Value",
            sortable: true,
            render: (row) => (
                row.displayValue || "-"
            ),
        },
        {
            key: "displayOrder",
            label: "Display Order",
            sortable: true,
            render: (row) => (
                row.displayOrder ?? "-"
            ),
            filter: true,
        },
        {
            key: "edit",
            label: "Edit",
            sortable: false,
            render: (row) => (
                <button
                    className="edit-btn"
                    title="Edit"
                    onClick={() => openEditModal(row)}
                >
                    <i className="fa-solid fa-pen-to-square" /> Edit
                </button>
            ),
        },
        {
            key: "delete",
            label: "Delete",
            sortable: false,
            render: (row) => (
                <button
                    className="delete-btn"
                    title="Delete"
                    onClick={() => handleDeleteLOV(row._id)}
                >
                    <i className="fa-solid fa-trash" /> 
                </button>
            ),
        },
    ];

    // ── shared form body ──────────────────────────────────────────────────────

    const renderFormFields = () => (
        <>
            {/* Type */}
            <div className="form-group">
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "5px" }}>
                    <label htmlFor="type" style={{ margin: 0 }}>
                        <i className="fa-solid fa-asterisk requiredIcon" /> Type
                    </label>
                    <button
                        type="button"
                        onClick={() => {
                            setIsNewType(!isNewType);
                            setFormData((prev) => ({ ...prev, type: "" }));
                        }}
                        style={{
                            background: "none",
                            border: "none",
                            color: "var(--primary-color, #007bff)",
                            cursor: "pointer",
                            fontSize: "0.85rem",
                            textDecoration: "underline",
                            padding: 0
                        }}
                    >
                        {isNewType ? "Select Existing" : "Create New Type"}
                    </button>
                </div>
                {isNewType ? (
                    <input
                        type="text"
                        id="type"
                        name="type"
                        value={formData.type}
                        onChange={handleInputChange}
                        placeholder="e.g. LLM_PROVIDERS"
                        required
                    />
                ) : (
                    <CreatableSelect
                        inputId="type"
                        name="type"
                        options={availableTypes}
                        value={formData.type ? { value: formData.type, label: formData.type } : null}
                        onChange={(sel) =>
                            setFormData((prev) => ({ ...prev, type: sel ? sel.value : "" }))
                        }
                        onCreateOption={(inputValue) => {
                            setIsNewType(true);
                            setFormData((prev) => ({ ...prev, type: inputValue.trim() }));
                        }}
                        placeholder="Select or type a new type..."
                        isClearable
                        formatCreateLabel={(inputValue) => `Create type "${inputValue}"`}
                    />
                )}
            </div>

            {/* Value */}
            <div className="form-group">
                <label htmlFor="value">
                    <i className="fa-solid fa-asterisk requiredIcon" /> Value
                </label>
                <input
                    type="text"
                    id="value"
                    name="value"
                    value={formData.value}
                    onChange={handleInputChange}
                    placeholder="e.g. GPT-4, Turbo"
                    required
                />
            </div>

            {/* Display Value */}
            <div className="form-group">
                <label htmlFor="displayValue">
                    Display Value
                </label>
                <input
                    type="text"
                    id="displayValue"
                    name="displayValue"
                    value={formData.displayValue}
                    onChange={handleInputChange}
                    placeholder="e.g. GPT-4 Turbo"
                />
            </div>

            {/* Display Order */}
            <div className="form-group">
                <label htmlFor="displayOrder">
                    <i className="fa-solid fa-asterisk requiredIcon" /> Display Order
                </label>
                <input
                    type="number"
                    id="displayOrder"
                    name="displayOrder"
                    value={formData.displayOrder}
                    onChange={handleInputChange}
                    placeholder="e.g. 1"
                    min="0"
                    required
                />
            </div>
        </>
    );

    // ── render ────────────────────────────────────────────────────────────────

    return (
        <>
            <Navbar
                createButtons={[
                    {
                        text: "Create LOV",
                        onClick: () => {
                            resetForm();
                            setIsCreateModalOpen(true);
                        },
                    },
                ]}
                pageName="LOVs"
                activePage="LOVs"
                breadcrumbs={breadcrumbs}
            />

            <div className="manage-token" style={{ maxWidth: "100%", margin: 0, padding: 0 }}>
                {/* Filter Section */}
                <div className="filter-section" style={{ padding: "20px", borderBottom: "1px solid #e0e0e0" }}>
                    <label style={{ marginRight: "15px", fontWeight: "500" }}>Filter by Type:</label>
                    <Select
                        id="typeFilter"
                        name="typeFilter"
                        options={availableTypes}
                        value={selectedType}
                        onChange={(sel) => {
                            setSelectedType(sel);
                            if (sel) {
                                setFilteredData(data.filter((lov) => lov.type === sel.value));
                            } else {
                                setFilteredData(data);
                            }
                        }}
                        placeholder="All Types"
                        isClearable
                        className="type-filter-select"
                    />
                </div>

                {/* Table Section */}
                <div className="table-section">
                    {loading ? (
                        <Loader style={{ fontSize: "2.2rem" }} />
                    ) : error ? (
                        <div className="error-container">
                            <div className="error-icon">⚠️</div>
                            <h2>Error Loading Data</h2>
                            <p>{error}</p>
                            <button onClick={fetchLOVs} className="retry-btn">
                                <span>🔄</span> Retry
                            </button>
                        </div>
                    ) : (
                        <Table
                            data={[...filteredData].sort((a, b) => {
                                const typeCompare = (a.type ?? "").localeCompare(b.type ?? "");
                                if (typeCompare !== 0) return typeCompare;
                                return (a.displayOrder ?? Infinity) - (b.displayOrder ?? Infinity);
                            })}
                            columns={columns}
                            caption={`LOVs (${filteredData.length}/${data.length})`}
                        />
                    )}
                </div>
            </div>

            {/* ── Create Modal ────────────────────────────────────────────── */}
            <Modal
                isOpen={isCreateModalOpen}
                onClose={() => !isSubmitting && (setIsCreateModalOpen(false), resetForm())}
            >
                <h2>Create New LOV</h2>
                <form onSubmit={handleCreateLOV}>
                    {renderFormFields()}
                    {formError && <div className="error-message">{formError}</div>}
                    <button type="submit" className="btn-submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                            <Loader style={{ fontSize: "1em", color: "white", top: "0", left: "0" }} />
                        ) : (
                            <>
                                <i className="fa-solid fa-plus" /> Create LOV
                            </>
                        )}
                    </button>
                </form>
            </Modal>

            {/* ── Edit Modal ──────────────────────────────────────────────── */}
            <Modal
                isOpen={isEditModalOpen}
                onClose={() => !isSubmitting && (setIsEditModalOpen(false), resetForm())}
            >
                <h2>Edit LOV</h2>
                <form onSubmit={handleUpdateLOV}>
                    {renderFormFields()}
                    {formError && <div className="error-message">{formError}</div>}
                    <button type="submit" className="btn-submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                            <Loader style={{ fontSize: "1em", color: "white", top: "0", left: "0" }} />
                        ) : (
                            <>
                                <i className="fa-solid fa-floppy-disk" /> Update LOV
                            </>
                        )}
                    </button>
                </form>
            </Modal>

            <style>{`
                .filter-section {
                    background: var(--card-bg-secondary, rgba(255,255,255,0.03));
                    display: flex;
                    align-items: center;
                    gap: 15px;
                }
                .type-filter-select {
                    min-width: 300px !important;
                }
                .delete-btn {
                    background-color: #dc3545;
                    color: white;
                    padding: 6px 12px;
                    border: none;
                    border-radius: 4px;
                    cursor: pointer;
                    font-size: 0.85rem;
                }
                .delete-btn:hover {
                    opacity: 0.85;
                }
                .error-message {
                    color: #dc3545;
                    padding: 10px;
                    margin: 10px 0;
                    border: 1px solid #dc3545;
                    border-radius: 4px;
                    background: rgba(220, 53, 69, 0.1);
                }
            `}</style>
        </>
    );
}
